import { useState, type ReactNode, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { getUserData } from '@/services/api';
import { useLowStockAlerts, useCurrentUser } from '@/hooks/useQueries';
import { performLogout } from '@/utils/auth.ts';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';

interface DashboardLayoutProps {
  children: ReactNode;
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(60);
  const location = useLocation();
  const navigate = useNavigate();
  const notificationRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  // Initialize from localStorage first for immediate UI update
  const initialUserData = getUserData();
  const [userRole, setUserRole] = useState<string>(initialUserData?.role || 'staff');
  const [isAdmin, setIsAdmin] = useState(initialUserData?.role === 'admin');
  const [userName, setUserName] = useState<string>(initialUserData?.name || '');
  const [userEmail, setUserEmail] = useState<string>(initialUserData?.email || '');

  // Get user info using React Query
  const { data: currentUser, isLoading: userLoading } = useCurrentUser();
  
  useEffect(() => {
    if (currentUser) {
      const role = currentUser.role || 'staff';
      setUserRole(role);
      setIsAdmin(role === 'admin');
      setUserName(currentUser.name || '');
      setUserEmail(currentUser.email || '');
      
      if (import.meta.env.DEV) {
        console.log('User role verified:', role, 'isAdmin:', role === 'admin');
      }
    } else if (!userLoading) {
      // If query failed and not loading, check localStorage
      const userData = getUserData();
      if (userData) {
        const role = userData.role || 'staff';
        setUserRole(role);
        setIsAdmin(role === 'admin');
        setUserName(userData.name || '');
        setUserEmail(userData.email || '');
        } else {
          performLogout(queryClient, navigate);
        }
    }
  }, [currentUser, userLoading, navigate]);

  // Use React Query for low stock alerts
  const { data: lowStockAlertsData } = useLowStockAlerts();
  const [lowStockAlerts, setLowStockAlerts] = useState<Array<{
    id: number;
    name: string;
    current: number;
    minimum: number;
    category: string;
  }>>([]);

  const allNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: 'ri-dashboard-line', roles: ['admin', 'staff'] },
    { name: 'Receive Stock', href: '/receive', icon: 'ri-inbox-line', roles: ['admin'] },
    { name: 'Issue Stock', href: '/issue', icon: 'ri-send-plane-line', roles: ['admin', 'staff'] },
    { name: 'Visits', href: '/visits', icon: 'ri-calendar-check-line', roles: ['admin', 'staff'] },
    { name: 'Invoices', href: '/invoices', icon: 'ri-file-list-3-line', roles: ['admin', 'staff'] },
    { name: 'Inventory', href: '/inventory', icon: 'ri-archive-line', roles: ['admin'] },
    { name: 'Categories', href: '/categories', icon: 'ri-folder-line', roles: ['admin'] },
    { name: 'Locations', href: '/locations', icon: 'ri-map-pin-line', roles: ['admin'] },
    { name: 'Reports', href: '/reports', icon: 'ri-bar-chart-box-line', roles: ['admin'] },
    { name: 'Users', href: '/users', icon: 'ri-team-line', roles: ['admin'] },
    { name: 'Settings', href: '/settings', icon: 'ri-settings-3-line', roles: ['admin', 'staff'] }
  ];

  // Filter navigation based on user role
  const navigation = allNavigation.filter(item => item.roles.includes(userRole));

  const isActive = (path: string) => location.pathname === path;
  
  const handleLogout = async () => {
    await performLogout(queryClient, navigate);
  };

  // Idle timeout handler - 10 minutes
  const handleIdleWarning = (seconds: number) => {
    setRemainingSeconds(seconds);
    setShowIdleWarning(true);
  };

  const handleIdleLogout = () => {
    setShowIdleWarning(false);
    handleLogout();
  };

  const handleStayLoggedIn = () => {
    setShowIdleWarning(false);
    // Reset the timer by triggering activity
    if (resetTimer) {
      resetTimer();
    }
  };

  // Use idle timeout hook 
  const { resetTimer } = useIdleTimeout({
    timeout: 15 * 60 * 1000, 
    warningTime: 60 * 1000, 
    onWarning: handleIdleWarning,
    onIdle: handleIdleLogout,
  });

  // Update countdown timer
  useEffect(() => {
    if (!showIdleWarning) return;

    const interval = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setShowIdleWarning(false);
          performLogout(queryClient, navigate);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showIdleWarning, queryClient, navigate]);

  const handleClearNotifications = () => {
    setLowStockAlerts([]);
    setNotificationOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setNotificationOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    if (notificationOpen || userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [notificationOpen, userMenuOpen]);

  // Check if low stock alerts are enabled
  const isLowStockAlertsEnabled = () => {
    const savedNotifications = localStorage.getItem('notificationPreferences');
    if (savedNotifications) {
      try {
        const prefs = JSON.parse(savedNotifications);
        return prefs.lowStockAlerts !== false; // Default to true if not set
      } catch (error) {
        return true; // Default to enabled if parsing fails
      }
    }
    return true; // Default to enabled if no preference is saved
  };

  // Process low stock alerts data from React Query
  useEffect(() => {
    if (!isLowStockAlertsEnabled()) {
      setLowStockAlerts([]);
      return;
    }

    if (Array.isArray(lowStockAlertsData)) {
      const transformedAlerts = lowStockAlertsData.map((item: any) => ({
        id: item.id,
        name: item.name || 'Unknown Item',
        current: item.quantity || 0,
        minimum: item.min_quantity || 0,
        category: item.category?.name || 'Uncategorized'
      }));
      setLowStockAlerts(transformedAlerts);
    } else {
      setLowStockAlerts([]);
    }
  }, [lowStockAlertsData]);
  
  // Check preferences periodically to catch changes
  useEffect(() => {
    const checkPreferencesInterval = setInterval(() => {
      if (!isLowStockAlertsEnabled() && lowStockAlerts.length > 0) {
        setLowStockAlerts([]);
      }
    }, 1000);
    
    // Also listen for storage changes (across tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'notificationPreferences' && !isLowStockAlertsEnabled()) {
        setLowStockAlerts([]);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      clearInterval(checkPreferencesInterval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [lowStockAlerts.length]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-sky-200 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-sky-200">
            <Link to="/dashboard" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden">
                <img 
                  src="/images/hic-logo.png" 
                  alt="Heart Institute of the Caribbean"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h1 className="text-lg font-bold text-sky-900">HIC | XtraCare</h1>
                <p className="text-xs text-sky-600">Management</p>
              </div>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden w-8 h-8 flex items-center justify-center text-sky-600 hover:text-sky-800"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 overflow-y-auto">
            <ul className="space-y-1">
              {navigation.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive(item.href)
                        ? 'bg-sky-500 text-white shadow-md'
                        : 'text-sky-700 hover:bg-sky-200'
                    }`}
                  >
                    <i className={`${item.icon} text-lg`}></i>
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* User Profile */}
          <div className="px-4 py-4 border-t border-sky-200">
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-sky-200/50">
              <div className="w-10 h-10 bg-sky-500 rounded-full flex items-center justify-center text-white font-semibold">
                {isAdmin ? 'AD' : 'ST'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-sky-900 truncate">
                  {userName || (isAdmin ? 'Admin User' : 'Staff User')}
                </p>
                <p className="text-xs text-sky-600 truncate">{userEmail || 'No email'}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Navigation */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between px-6 py-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-700 hover:bg-gray-100 rounded-lg cursor-pointer"
            >
              <i className="ri-menu-line text-xl"></i>
            </button>

            <div className="flex-1"></div>

            <div className="flex items-center gap-3">
              <div className="relative" ref={notificationRef}>
                  <button 
                    onClick={() => {
                      // Check preferences when opening notification panel
                      if (!isLowStockAlertsEnabled()) {
                        setLowStockAlerts([]);
                      }
                      setNotificationOpen(!notificationOpen);
                    }}
                    className="relative w-10 h-10 flex items-center justify-center text-gray-700 hover:bg-gray-100 rounded-lg cursor-pointer"
                  >
                    <i className="ri-notification-3-line text-xl"></i>
                    {isLowStockAlertsEnabled() && lowStockAlerts.length > 0 && (
                      <span className="absolute top-1 right-1 w-2 h-2 bg-[#eb1539] rounded-full"></span>
                    )}
                  </button>

                {/* Notification Panel */}
                {notificationOpen && (
                  <div className="fixed sm:absolute left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-0 top-20 sm:top-full sm:mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-50 w-[calc(100vw-2rem)] sm:w-auto sm:min-w-[20rem] max-w-[20rem] sm:max-w-sm mx-auto sm:mx-0">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                      <h3 className="text-sm font-semibold text-gray-900">Low Stock Alerts</h3>
                      <span className="px-2 py-1 bg-[#eb1539] text-white text-xs font-medium rounded-full">
                        {lowStockAlerts.length}
                      </span>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {lowStockAlerts.length > 0 ? (
                        lowStockAlerts.map((alert) => (
                          <div
                            key={alert.id}
                            className="px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                            onClick={() => {
                              setNotificationOpen(false);
                              window.location.href = '/inventory?status=low';
                            }}
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 bg-[#eb1539]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                <i className="ri-alert-line text-[#eb1539] text-sm"></i>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {alert.name}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">{alert.category}</p>
                                <div className="flex items-center gap-2 mt-2">
                                  <span className="text-xs text-[#eb1539] font-medium">
                                    Current: {alert.current}
                                  </span>
                                  <span className="text-xs text-gray-400">•</span>
                                  <span className="text-xs text-gray-600">
                                    Min: {alert.minimum}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-8 text-center">
                          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <i className="ri-notification-off-line text-gray-400 text-xl"></i>
                          </div>
                          <p className="text-sm text-gray-500">No notifications</p>
                        </div>
                      )}
                    </div>
                    {lowStockAlerts.length > 0 && (
                      <div className="px-4 py-3 border-t border-gray-200">
                        <button
                          onClick={handleClearNotifications}
                          className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-[#eb1539] text-white text-sm font-medium rounded-lg hover:bg-[#c01030] transition-colors whitespace-nowrap cursor-pointer"
                        >
                          Clear Notifications
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="w-10 h-10 bg-[#eb1539] rounded-full flex items-center justify-center text-white font-semibold cursor-pointer"
                  title={`${userName || (isAdmin ? 'Admin' : 'Staff')} - ${userEmail}`}
                >
                  {userName ? userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : (isAdmin ? 'AD' : 'ST')}
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    <div className="px-4 py-3 border-b border-gray-200">
                      <p className="text-sm font-medium text-gray-900 truncate">{userName || 'User'}</p>
                    </div>
                    <Link
                      to="/settings"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <i className="ri-user-settings-line"></i>
                      Profile
                    </Link>
                    <hr className="my-2 border-gray-200" />
                    <button 
                      onClick={handleLogout}
                      className="flex items-center gap-3 px-4 py-2 text-sm text-[#eb1539] hover:bg-gray-100 w-full text-left cursor-pointer"
                    >
                      <i className="ri-logout-box-line"></i>
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 sm:p-6">
          {children}
        </main>
      </div>

      {/* Idle Timeout Warning Modal */}
      {showIdleWarning && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-time-line text-yellow-600 text-2xl"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Session Timeout Warning
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  You've been inactive for a while. Your session will expire in{' '}
                  <span className="font-semibold text-[#eb1539]">{remainingSeconds}</span> seconds.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={handleStayLoggedIn}
                    className="flex-1 px-4 py-2 bg-[#50b3e1] text-white rounded-lg hover:bg-[#3a9bc1] transition-colors font-medium"
                  >
                    Stay Logged In
                  </button>
                  <button
                    onClick={handleIdleLogout}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Logout Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
